/**
 * SourceFields: where one benchmark's bytes are. A kind select, then the
 * fields that kind needs: a repository picked from the team's GitHub App
 * installation plus a ref, or one of the team's S3 connections plus a
 * prefix and the prefixes under it to leave out.
 *
 * The repository is picked, not typed: the page reads the installation the
 * way Settings > GitHub App does and offers the repositories it covers.
 * Picking one prefills the ref with the repository's default branch.
 */

import type {
  ConnectionAdminItem,
  GithubAppRepository,
  GithubAppStatus,
} from '@/api/generated/model'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

import {
  SOURCE_KIND_LABELS,
  SOURCE_KINDS,
  newSource,
  type SourceKind,
  type SourceRow,
} from './benchmarkSources'
import { TextField } from './TextField'

export interface SourceFieldsProps {
  /** Prefix for the inputs' ids, unique on the page. */
  id: string
  source: SourceRow
  github: GithubAppStatus | undefined
  githubPending: boolean
  /** The team's S3 connections, the ones an S3 source may read through. */
  connections: ConnectionAdminItem[]
  /** The repository is set: open eval PRs are resolved against it. */
  frozenRepo: boolean
  onChange: (source: SourceRow) => void
}

export function SourceFields({
  id,
  source,
  github,
  githubPending,
  connections,
  frozenRepo,
  onChange,
}: SourceFieldsProps): React.JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor={`${id}-kind`}>Read from</Label>
        <Select
          value={source.kind}
          disabled={frozenRepo}
          onValueChange={(next) => {
            // Radix only emits the values of the items below. A repository
            // is not a prefix, so the fields start over with the kind.
            onChange(newSource(next as SourceKind))
          }}
        >
          <SelectTrigger id={`${id}-kind`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_KINDS.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {SOURCE_KIND_LABELS[kind]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {source.kind === 'github' ? (
        <>
          <RepositoryPicker
            id={`${id}-repo`}
            value={source.repo}
            github={github}
            pending={githubPending}
            frozen={frozenRepo}
            onPick={(repo) => {
              // The ref follows the repository; staff can still change it after.
              onChange({ ...source, repo: repo.full_name, ref: repo.default_branch })
            }}
          />
          <TextField
            id={`${id}-ref`}
            label="Branch or tag"
            value={source.ref}
            mono
            placeholder="main"
            hint="Picking a repository fills in its default branch."
            onChange={(ref) => {
              onChange({ ...source, ref })
            }}
          />
        </>
      ) : (
        <>
          <ConnectionPicker
            id={`${id}-connection`}
            value={source.connection_id}
            connections={connections}
            onPick={(connection_id) => {
              onChange({ ...source, connection_id })
            }}
          />
          <TextField
            id={`${id}-prefix`}
            label="S3 prefix"
            value={source.prefix}
            mono
            placeholder="s3://bucket/path/to/the/benchmark"
            hint="Name the directory under the bucket that holds the benchmark. Leave off the trailing slash."
            onChange={(prefix) => {
              onChange({ ...source, prefix })
            }}
          />
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={`${id}-ignore`}>Ignored prefixes</Label>
            <Textarea
              id={`${id}-ignore`}
              className="font-mono"
              value={source.ignore}
              rows={2}
              placeholder={'build_logs/'}
              aria-describedby={`${id}-ignore-hint`}
              onChange={(e) => {
                onChange({ ...source, ignore: e.target.value })
              }}
            />
            <p id={`${id}-ignore-hint`} className="text-xs text-muted-foreground">
              List one prefix per line, relative to the S3 prefix. Objects under them are left
              out of the fetch and the revision.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

/** GitHub repository names are case-insensitive. */
function sameRepo(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

/** The repositories the installation covers; none until one is linked and reachable. */
function coveredRepositories(github: GithubAppStatus | undefined): GithubAppRepository[] {
  return github?.installation?.repositories ?? []
}

/**
 * A select over the installation's repositories. The stored value always
 * has an entry, so the picker reads what the night would check out even
 * when the installation no longer covers it, and a change is one pick.
 */
function RepositoryPicker({
  id,
  value,
  github,
  pending,
  frozen,
  onPick,
}: {
  id: string
  value: string
  github: GithubAppStatus | undefined
  pending: boolean
  frozen: boolean
  onPick: (repo: GithubAppRepository) => void
}): React.JSX.Element {
  const covered = coveredRepositories(github)
  // GitHub repository names are case-insensitive, so a stored `Acme/Agent`
  // is the listed `acme/agent`, shown under the listing's spelling.
  const listed = covered.find((repo) => sameRepo(repo.full_name, value))
  // The stored repository stays pickable when the installation has no
  // list or has dropped it; it just keeps the ref that is set. An entry
  // with no repository yet holds the installation's list alone.
  const options: GithubAppRepository[] =
    listed === undefined && value !== ''
      ? [{ full_name: value, html_url: '', private: false, default_branch: '' }, ...covered]
      : covered
  const installed = github?.status === 'installed'
  const disabled = frozen || pending || covered.length === 0
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Repository</Label>
      <Select
        value={listed === undefined ? value : listed.full_name}
        disabled={disabled}
        onValueChange={(picked) => {
          const repo = covered.find((candidate) => candidate.full_name === picked)
          if (repo !== undefined) onPick(repo)
        }}
      >
        <SelectTrigger id={id} className="font-mono" aria-describedby={`${id}-hint`}>
          <SelectValue placeholder="No repository yet" />
        </SelectTrigger>
        <SelectContent>
          {options.map((repo) => (
            <SelectItem key={repo.full_name} value={repo.full_name} className="font-mono">
              {repo.full_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {frozen ? (
          <>It was set with the eval PR layout. Open eval PRs are resolved against it.</>
        ) : (
          <RepositoryHint github={github} pending={pending} installed={installed} />
        )}
      </p>
      {installed &&
        github.eval_pr_repo_covered === false &&
        github.eval_pr_repo !== null &&
        sameRepo(github.eval_pr_repo, value) && (
          <p className="text-sm text-destructive" role="alert">
            The installation cannot reach this repository. Add it on the installation page on
            GitHub, or pick another one.
          </p>
        )}
    </div>
  )
}

function RepositoryHint({
  github,
  pending,
  installed,
}: {
  github: GithubAppStatus | undefined
  pending: boolean
  installed: boolean
}): React.JSX.Element {
  if (pending) return <>The installation is being read on GitHub.</>
  if (!installed || github?.installation === null || github === undefined) {
    return <>Link the team&apos;s GitHub App installation above to pick a repository.</>
  }
  if (github.installation.repositories.length === 0) {
    return <>The installation covers no repositories yet. Add one on the installation page on GitHub.</>
  }
  return <>Every repository the installation covers is listed.</>
}

/**
 * A select over the team's S3 connections. The jobs read the prefix
 * through the picked one, so only this team's connections are offered.
 */
function ConnectionPicker({
  id,
  value,
  connections,
  onPick,
}: {
  id: string
  value: string
  connections: ConnectionAdminItem[]
  onPick: (connectionId: string) => void
}): React.JSX.Element {
  const known = connections.some((connection) => connection.id === value)
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>S3 connection</Label>
      <Select
        value={known ? value : ''}
        disabled={connections.length === 0}
        onValueChange={(picked) => {
          onPick(picked)
        }}
      >
        <SelectTrigger id={id} aria-describedby={`${id}-hint`}>
          <SelectValue placeholder="No connection picked" />
        </SelectTrigger>
        <SelectContent>
          {connections.map((connection) => (
            <SelectItem key={connection.id} value={connection.id}>
              {connection.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {connections.length === 0
          ? 'This team has no S3 connection yet. Assign one on the Connections page first.'
          : 'The jobs read the prefix through this connection.'}
      </p>
    </div>
  )
}
